import { reloadPage, setTimeout, getDocument, popFromLocalStorage, setToLocalStorage } from '../../common/dom-helpers'
import { onWindowUnload } from '../../common/helpers/window'
import { getShellLogger } from '../../common/logger'
import { getShellApiInstance } from '../../common/shell-api-helpers'
import { canReloadApplication } from '../auth/can-reload'
import { getDesktopVersion } from '../container/helpers'
import { getFeatureFlagValue } from '../feature-flags'
import { FeatureFlagsVariations } from '../feature-flags/models'
import type { UpdateEvents } from '../update'
import { UpdateNamespace } from '../update'
import { getAutoUpdate, quitAndInstall } from '../update/helpers'

interface AppUpdateInfo {
  readonly shellVersion: string
  readonly containerVersion?: string
  readonly startTime: number
}

const APP_UPDATE_INFO_KEY = 'shell-app-update-start-time'
const DEFAULT_INTERVAL_VALUE = '3600000' //one hour in milliseconds
const REFRESH_INTERVAL_IN_HOURS = 24
const MINUTES_IN_AN_HOUR = 60
const SECONDS_IN_A_MINUTE = 60
const MILLISECONDS_IN_A_SECOND = 1000
const intervalInMilliseconds =
  REFRESH_INTERVAL_IN_HOURS * MINUTES_IN_AN_HOUR * SECONDS_IN_A_MINUTE * MILLISECONDS_IN_A_SECOND
let containerUpdateIsAvailable = false
let timeoutID: number | null = null

// Exported for testing purposes only
export const clearUpdateTimer = () => {
  if (timeoutID) {
    clearTimeout(timeoutID)
    timeoutID = null
  }
}

const createTimer = (interval = intervalInMilliseconds) => {
  clearUpdateTimer()
  timeoutID = setTimeout(() => {
    if (getDocument().visibilityState === 'hidden') {
      reloadIfAppIsIdle()
    } else {
      const visibilityChangeHandle = () => {
        if (getDocument().visibilityState === 'hidden') {
          getDocument().removeEventListener('visibilitychange', visibilityChangeHandle)
          reloadIfAppIsIdle()
        }
      }

      getDocument().addEventListener('visibilitychange', visibilityChangeHandle)
    }
  }, interval)
}

export const setupAppUpdateTimer = () => {
  listenToUpdateAvailableEvent()
  postAppUpdateLog()
  if (!timeoutID) {
    createTimer()
  }
}

const reloadIfAppIsIdle = async () => {
  const canReload = await canReloadApplication()
  if (canReload) {
    await setAppUpdateInfoInLocalStorage()
    if (containerUpdateIsAvailable) {
      quitAndInstall()
    } else {
      reloadPage()
    }
  }
}

const listenToUpdateAvailableEvent = () => {
  const { eventBus } = getShellApiInstance()
  const { updateAvailable } = eventBus.subscribeTo<typeof UpdateNamespace, typeof UpdateEvents>(UpdateNamespace)

  const updateAvailableHandler = () => {
    containerUpdateIsAvailable = true
    const refreshIntervalOverride = getFeatureFlagValue(
      FeatureFlagsVariations.SHELL_APP_UPDATE_INTERVAL_OVERRIDE,
      DEFAULT_INTERVAL_VALUE,
    )
    if (refreshIntervalOverride) {
      createTimer(Number(refreshIntervalOverride))
    }
  }

  updateAvailable.on(updateAvailableHandler)

  onWindowUnload(() => {
    updateAvailable.removeListener(updateAvailableHandler)
  })

  const update = getAutoUpdate()
  if (update) {
    updateAvailableHandler()
  }
}

const postAppUpdateLog = async () => {
  const appUpdateInfo = popFromLocalStorage(APP_UPDATE_INFO_KEY)
  if (!appUpdateInfo) {
    return
  }
  try {
    const { shellVersion, containerVersion, startTime } = JSON.parse(appUpdateInfo) as AppUpdateInfo
    getShellLogger().log(`App updated: Shell Version ${getShellApiInstance().meta.shellVersion} from ${shellVersion}`)
    const latestContainerVersion = await getDesktopVersion()
    if (containerVersion && latestContainerVersion) {
      getShellLogger().log(`App updated: Container Version ${latestContainerVersion} from ${containerVersion}`)
    }
    getShellLogger().log(`App update started at ${startTime}. Completed at: ${Date.now()}`)
  } catch {
    getShellLogger().error('Error deserializing appUpdateInfo')
  }
}

const setAppUpdateInfoInLocalStorage = async () => {
  const appUpdateInfo: AppUpdateInfo = {
    shellVersion: getShellApiInstance().meta.shellVersion,
    startTime: Date.now(),
    containerVersion: await getDesktopVersion(),
  }
  setToLocalStorage(APP_UPDATE_INFO_KEY, JSON.stringify(appUpdateInfo))
}
